import { router } from '@inertiajs/react';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/components/toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { toastFlash } from '@/lib/flash-toast';
import { cn } from '@/lib/utils';
import type { JudgingCriterion } from '@/types';

type Row = {
  key: string;
  id: string | null;
  name: string;
  weight: string;
  description: string;
};

const TOTAL = 100;

function rowsFrom(criteria: JudgingCriterion[]): Row[] {
  return criteria.map((criterion) => ({
    key: criterion.id,
    id: criterion.id,
    name: criterion.name,
    weight: String(criterion.weight),
    description: criterion.description ?? '',
  }));
}

// What is saved of a row, to compare the table against the saved set.
const saved = ({ id, name, weight, description }: Row) => ({
  id,
  name,
  weight,
  description,
});

// The criteria are edited as one table and saved as one set: the weights only make
// sense together, so the server refuses any set that does not add up to exactly 100%
// (JudgingCriteria) and the Save button says so before it gets there.
export function CriteriaEditor({
  eventSlug,
  criteria,
}: {
  eventSlug: string;
  criteria: JudgingCriterion[];
}) {
  const [rows, setRows] = useState<Row[]>(() => rowsFrom(criteria));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [processing, setProcessing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const total = rows.reduce((sum, row) => sum + (Number(row.weight) || 0), 0);
  const dirty =
    JSON.stringify(rows.map(saved)) !==
    JSON.stringify(rowsFrom(criteria).map(saved));
  const removed = criteria.filter(
    (criterion) => !rows.some((row) => row.id === criterion.id),
  );

  const update = (key: string, patch: Partial<Row>) =>
    setRows(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const addRow = () =>
    setRows([
      ...rows,
      {
        key: crypto.randomUUID(),
        id: null,
        name: '',
        weight: '',
        description: '',
      },
    ]);

  const save = () => {
    setConfirmingDelete(false);
    setProcessing(true);
    router.put(
      `/admin/${eventSlug}/judging/criteria`,
      {
        criteria: rows.map(({ id, name, weight, description }) => ({
          id: id ?? '',
          name,
          weight,
          description,
        })),
      },
      {
        preserveScroll: true,
        // The page keys this editor on the saved set, so a successful save remounts it
        // from the fresh `criteria` prop.
        onSuccess: (page) => toastFlash(page, 'Criteria saved'),
        onError: (serverErrors) => {
          setErrors(serverErrors as unknown as Record<string, string>);
          toast({ type: 'error', description: 'Failed to save criteria' });
        },
        onFinish: () => setProcessing(false),
      },
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Criteria ({rows.length})</CardTitle>
        <Button variant="outline" size="sm" onClick={addRow}>
          <Plus className="mr-2 h-4 w-4" />
          Add Criterion
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {rows.length === 0 ? (
          <p className="py-6 text-center text-muted-foreground">
            No criteria yet. Add the first one.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-40">Weight (%)</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={row.key}>
                  <TableCell className="text-muted-foreground">
                    {index + 1}
                  </TableCell>
                  <TableCell>
                    <Input
                      aria-label={`Criterion ${index + 1} name`}
                      placeholder="Innovation"
                      value={row.name}
                      onChange={(e) => update(row.key, { name: e.target.value })}
                    />
                    {errors[`criteria.${index}.name`] && (
                      <p className="mt-1 text-destructive text-sm">
                        {errors[`criteria.${index}.name`]}
                      </p>
                    )}
                    <Textarea
                      aria-label={`Criterion ${index + 1} description`}
                      placeholder="Description (optional) — judges see it when they hover the name"
                      rows={2}
                      maxLength={1000}
                      className="mt-2 text-sm"
                      value={row.description}
                      onChange={(e) =>
                        update(row.key, { description: e.target.value })
                      }
                    />
                    {errors[`criteria.${index}.description`] && (
                      <p className="mt-1 text-destructive text-sm">
                        {errors[`criteria.${index}.description`]}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    <Input
                      aria-label={`Criterion ${index + 1} weight`}
                      type="number"
                      min={1}
                      max={100}
                      step={1}
                      placeholder="25"
                      value={row.weight}
                      onChange={(e) =>
                        update(row.key, { weight: e.target.value })
                      }
                    />
                    {errors[`criteria.${index}.weight`] && (
                      <p className="mt-1 text-destructive text-sm">
                        {errors[`criteria.${index}.weight`]}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      aria-label={`Remove criterion ${index + 1}`}
                      onClick={() =>
                        setRows(rows.filter((other) => other.key !== row.key))
                      }
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <div className="flex flex-wrap items-center justify-between gap-4">
          <p
            className={cn(
              'font-medium text-sm',
              total === TOTAL ? 'text-green-600' : 'text-destructive',
            )}
          >
            Total: {total}%
            {total < TOTAL && ` — ${TOTAL - total}% missing`}
            {total > TOTAL && ` — ${total - TOTAL}% over`}
          </p>
          <div className="flex items-center gap-2">
            {dirty && (
              <Button
                variant="outline"
                disabled={processing}
                onClick={() => {
                  setRows(rowsFrom(criteria));
                  setErrors({});
                }}
              >
                Discard
              </Button>
            )}
            <Button
              disabled={processing || !dirty || total !== TOTAL}
              onClick={() =>
                removed.length > 0 ? setConfirmingDelete(true) : save()
              }
            >
              {processing ? 'Saving...' : 'Save Criteria'}
            </Button>
          </div>
        </div>
        {errors.base && <p className="text-destructive text-sm">{errors.base}</p>}
      </CardContent>

      <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete criteria</AlertDialogTitle>
            <AlertDialogDescription>
              Removing {removed.map((criterion) => criterion.name).join(', ')}{' '}
              also deletes every score the judges gave on{' '}
              {removed.length === 1 ? 'it' : 'them'}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={save}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete and save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
